import { useCallback } from 'react';
import {
  useFolderClipboardStore,
  useMailClipboardStore,
  type ClipboardFolder,
  type ClipboardMail,
} from '@/stores/clipboardStore';
import { useCopyMail, useMoveMail } from '@/hooks/useMailMutations';
import { useCopyFolder, useMoveFolder } from '@/hooks/useFolderMutations';
import { useNotify } from '@/features/notifications/useNotify';

/**
 * Copier / Couper / Coller des menus contextuels, pour les mails ET les dossiers.
 *
 * UN SEUL « Coller » : le presse-papiers est exclusif (cf. `clipboardStore`), donc
 * `pasteInto` n'a jamais à choisir entre deux contenus. Il colle ce qui a été
 * copié en dernier — des mails ou un dossier — dans le dossier cible.
 *
 * Coller reste réservé à un DOSSIER cible : ni `copy-mail`, ni `move-mail`, ni
 * `copy-folder` n'acceptent la racine d'un projet comme destination. Les points
 * d'appel désactivent donc « Coller » quand ils n'ont pas d'id de dossier.
 *
 * TOUT ÉCHEC EST DIT À L'UTILISATEUR. Les refus du serveur sont ici des messages
 * écrits pour être lus (« ce dossier contient 400 mails : la copie est limitée à
 * 300 », « un dossier de même nom existe déjà »…). Sans `onError`, ils
 * disparaissaient : le clic semblait ne rien faire, ce qui invite à recliquer — et
 * un second clic sur une copie déclenche une SECONDE copie.
 */
export function useItemClipboard() {
  const mailMode = useMailClipboardStore((s) => s.mode);
  const mailItems = useMailClipboardStore((s) => s.items);
  const copyMails = useMailClipboardStore((s) => s.copyMails);
  const cutMails = useMailClipboardStore((s) => s.cutMails);
  const clearMails = useMailClipboardStore((s) => s.clear);

  const folderMode = useFolderClipboardStore((s) => s.mode);
  const clipFolder = useFolderClipboardStore((s) => s.folder);
  const copyFolderToClipboard = useFolderClipboardStore((s) => s.copyFolder);
  const cutFolderToClipboard = useFolderClipboardStore((s) => s.cutFolder);
  const clearFolder = useFolderClipboardStore((s) => s.clear);

  const copyMail = useCopyMail();
  const moveMail = useMoveMail();
  const copyFolder = useCopyFolder();
  const moveFolder = useMoveFolder();
  const { notify, notifyError } = useNotify();

  const hasMails = mailMode !== null && mailItems.length > 0;
  const hasFolder = folderMode !== null && clipFolder !== null;
  const canPaste = hasMails || hasFolder;
  // Une copie de dossier peut durer des dizaines de secondes (une copie d'objet
  // S3 par mail). Les points d'appel s'en servent pour désactiver « Coller » :
  // sans cela, rien à l'écran ne distingue « en cours » de « n'a rien fait ».
  const isBusy = copyFolder.isPending || moveFolder.isPending;

  const pasteInto = useCallback(
    (targetFolderId: string, targetContainerId?: string) => {
      // --- Un dossier dans le presse-papiers -------------------------------
      if (hasFolder && clipFolder) {
        // Coller un dossier dans lui-même ou dans son propre parent (en coupé)
        // n'a pas de sens : le serveur refuserait le premier cas et le second
        // serait un déplacement sur place.
        if (clipFolder.folderId === targetFolderId) return;
        const crossContainer =
          targetContainerId && targetContainerId !== clipFolder.containerId
            ? targetContainerId
            : undefined;
        if (folderMode === 'cut') {
          if (clipFolder.parentId === targetFolderId) return;
          moveFolder.mutate(
            {
              folderId: clipFolder.folderId,
              newParentId: targetFolderId,
              newContainerId: crossContainer,
            },
            {
              // Un « couper » est consommé par le premier collage RÉUSSI. En cas
              // d'échec (cycle refusé, par exemple) le presse-papiers est
              // CONSERVÉ, pour que l'utilisateur puisse viser ailleurs.
              onSuccess: () => clearFolder(),
              onError: (err) =>
                notifyError('Déplacement impossible', err instanceof Error ? err.message : String(err)),
            },
          );
        } else {
          copyFolder.mutate(
            {
              folderId: clipFolder.folderId,
              targetParentId: targetFolderId,
              targetContainerId: crossContainer,
            },
            {
              onSuccess: (res) => {
                // Le serveur a pu RENOMMER la copie (« (copie) ») et n'a pas
                // forcément tout pris : on annonce le résultat réel.
                const parts = [`« ${res.name} »`];
                if (res.folderCount > 1) parts.push(`${res.folderCount} dossiers`);
                if (res.mailCount > 0) parts.push(`${res.mailCount} mail(s)`);
                notify('success', 'Dossier copié', parts.join(' · '));
              },
              onError: (err) =>
                notifyError('Copie impossible', err instanceof Error ? err.message : String(err)),
            },
          );
        }
        return;
      }

      // --- Des mails dans le presse-papiers ---------------------------------
      if (!hasMails) return;
      const targets = mailItems.filter(
        // Un « couper » vers le dossier d'origine est un no-op (et un 409 côté
        // serveur) : on l'écarte avant de compter quoi que ce soit.
        (it) => !(mailMode === 'cut' && it.folderId === targetFolderId),
      );
      if (targets.length === 0) return;

      // On attend les RÉSULTATS avant de vider le presse-papiers. Compter les
      // envois suffisait à le vider alors qu'un déplacement pouvait encore
      // échouer (doublon dans le dossier cible, par exemple) : les mails
      // restaient à leur place, mais l'utilisateur n'avait plus rien à recoller.
      //
      // Branche explicite plutôt qu'une mutation choisie dans une variable : les
      // deux mutations n'ont pas le même type de résultat.
      void Promise.allSettled(
        targets.map((it) => {
          const args = {
            mailId: it.mailId,
            sourceFolderId: it.folderId,
            targetFolderId,
          };
          return mailMode === 'cut'
            ? moveMail.mutateAsync(args).then(() => undefined)
            : copyMail.mutateAsync(args).then(() => undefined);
        }),
      ).then((results) => {
        const failed = results.filter((r) => r.status === 'rejected');
        const ok = results.length - failed.length;
        if (failed.length > 0) {
          const first = failed[0];
          const reason =
            first && first.status === 'rejected'
              ? first.reason instanceof Error
                ? first.reason.message
                : String(first.reason)
              : '';
          notifyError(
            mailMode === 'cut' ? 'Déplacement incomplet' : 'Copie incomplète',
            `${failed.length} mail(s) sur ${results.length} en échec${reason ? ` : ${reason}` : ''}`,
          );
        }
        // Un « couper » n'est consommé que si au moins un mail a réellement bougé.
        if (mailMode === 'cut' && ok > 0) clearMails();
      });
    },
    [
      hasFolder,
      hasMails,
      clipFolder,
      folderMode,
      mailMode,
      mailItems,
      moveFolder,
      copyFolder,
      moveMail,
      copyMail,
      clearFolder,
      clearMails,
      notify,
      notifyError,
    ],
  );

  return {
    canPaste,
    /** Ce que « Coller » collera : utile pour un libellé ou une infobulle. */
    pending: hasFolder ? ('folder' as const) : hasMails ? ('mails' as const) : null,
    mailCount: mailItems.length,
    copyMails,
    cutMails,
    copyFolder: copyFolderToClipboard,
    cutFolder: cutFolderToClipboard,
    pasteInto,
    /** Vrai pendant une copie ou un déplacement de dossier — opérations longues. */
    isBusy,
  };
}

export type { ClipboardMail, ClipboardFolder };
